import React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { dayjs } from '@mobi/utils'
import { Spinner } from '@mobi/component-library/Legacy/Spinner'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { MainContainer } from '@core/Components/Containers'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { useAuthenticatedAccount } from '@core/Components/RequireAuth/hooks'
import TakeABreakInfo from './Components/TakeABreakInfo'
import TakeABreakForm from './Components/TakeABreakForm'
import TakeABreakError from './Components/TakeABreakError'
import TakeABreakActive from './Components/TakeABreakActive'
import { saveTakeABreak, TakeABreakResponse } from './api'
import { useTakeABreak } from './Hooks'
import {
  trackTakeABreakCancel,
  trackTakeABreakSubmitFailed,
  trackTakeABreakSubmitSelection,
  trackTakeABreakSubmitSucceeded,
} from './helpers/analytics'

export const TakeABreak: React.FC = () => {
  const { accountNumber } = useAuthenticatedAccount()
  const queryClient = useQueryClient()
  const queryKey = [queryKeys.takeABreak, accountNumber]
  const [userHasAgreedToBreakTerms, setUserHasAgreedToBreakTerms] = React.useState(false)
  const justPostedAccountToTakeABreak = React.useRef(false)

  const getTakeABreakDetails = useTakeABreak()

  const setTakeABreakDetails = useMutation({
    mutationFn: saveTakeABreak,
    onSuccess: (data: TakeABreakResponse, variables) => {
      queryClient.setQueryData(queryKey, { ...data })
      justPostedAccountToTakeABreak.current = true
      trackTakeABreakSubmitSucceeded({ variables, accountNumber })
    },
    onError: (_, variables) => {
      justPostedAccountToTakeABreak.current = true
      trackTakeABreakSubmitFailed({ variables, accountNumber })
    },
  })

  const onPostAccountToTakeABreakHandler = (breakDuration: number) => {
    trackTakeABreakSubmitSelection({ accountNumber, breakDuration })
    setTakeABreakDetails.mutate({
      accountNumber: accountNumber ?? 0,
      endDate: dayjs().add(breakDuration, 'days').toDate(),
    })
  }

  const isLoading = getTakeABreakDetails.isLoading
  const inError = getTakeABreakDetails.isError || setTakeABreakDetails.isError
  const takeABreakIsActive = getTakeABreakDetails.isSuccess && getTakeABreakDetails.data?.inProgress
  const takeABreakIsNotActive =
    getTakeABreakDetails.isSuccess && !getTakeABreakDetails.data?.inProgress

  React.useEffect(() => {
    return () => {
      if (takeABreakIsNotActive && !justPostedAccountToTakeABreak.current) {
        // user opts not to take a break send GA
        // only trigger event if inactive and NOT after a post to set tab
        trackTakeABreakCancel({ accountNumber })
      }
    }
  }, [takeABreakIsNotActive, accountNumber])

  return (
    <MainContainer background='white' data-tid-take-a-break>
      <PageHeader title='Take a Break' />

      {isLoading && <Spinner data-testid='loading' />}

      {inError && <TakeABreakError data-testid='error' />}

      {takeABreakIsActive && getTakeABreakDetails.data && (
        <TakeABreakActive
          accountNumber={accountNumber}
          endDate={getTakeABreakDetails.data.endDate}
          justPostedAccountToTakeABreak={justPostedAccountToTakeABreak.current}
        />
      )}

      {takeABreakIsNotActive &&
        (!userHasAgreedToBreakTerms ? (
          <TakeABreakInfo
            setUserHasAgreedToBreakTerms={setUserHasAgreedToBreakTerms}
            accountNumber={accountNumber}
          />
        ) : (
          <TakeABreakForm
            onPostAccountToTakeABreakHandler={onPostAccountToTakeABreakHandler}
            isPostingAccountToTakeABreakBusy={setTakeABreakDetails.isPending}
            accountNumber={accountNumber}
          />
        ))}
    </MainContainer>
  )
}
